
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Copy, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";

export function OnboardingFormDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();
  const isLightMode = theme === 'light';

  const generateLink = async () => {
    try {
      setIsLoading(true);
      
      // Generate unique ID for onboarding form
      const onboardingUniqueId = uuidv4();
      
      // Get base URL for links
      const baseUrl = window.location.origin;
      // Create the unique link with the onboardingUniqueId
      const onboardingLink = `${baseUrl}/onboarding/${onboardingUniqueId}`;

      // Get current user's information to set as PIC
      let picArray: string[] = [];
      let picId = null;
      
      if (user) {
        // Fetch the current user's name from kol_pic table
        const { data: userData, error: userError } = await supabase
          .from('kol_pic')
          .select('id, name')
          .eq('id', user.id)
          .single();
          
        if (!userError && userData) {
          picArray = [userData.name];
          picId = userData.id;
        }
      }

      // Initialize all phase statuses
      const phaseStatuses = {
        status_phase_1: 'pending',
        status_phase_2: 'pending',
        status_phase_3: 'pending',
        status_phase_4: 'pending',
        status_phase_5: 'pending',
        status_phase_6: 'pending',
        status_phase_7: 'pending'
      };

      // Create a minimal onboarding form entry
      const { data: onboardingForm, error: onboardingError } = await supabase
        .from('onboarding_forms')
        .insert({
          id: onboardingUniqueId,
          kol_name: "To be filled by creator",
          unique_link: onboardingLink,
          content_links: [],
          status: 'pending',
          ...phaseStatuses,
          pic: picArray,  // Set current user as PIC
          pic_id: picId   // Set current user's ID as pic_id
        })
        .select()
        .single();

      if (onboardingError) throw onboardingError;
      
      // Create entry in onboarding_links table
      const { error: linkError } = await supabase
        .from('onboarding_links')
        .insert({
          form_id: onboardingForm.id,
          unique_link: onboardingLink,
          status: 'active'
        });
        
      if (linkError) {
        console.error("Error creating onboarding link:", linkError);
        throw linkError;
      }
      
      setGeneratedLink(onboardingLink);
      toast.success("Link generated successfully");
      
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error("Failed to generate link");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      toast.success("Link copied to clipboard");
    }
  };

  const closeAndRefresh = () => {
    setIsOpen(false);
    setGeneratedLink(null);
    navigate('/kol-compensation', { replace: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={isLightMode ? "outline" : "default"} 
          className={`flex items-center gap-2 ${isLightMode ? 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50' : ''}`}
        >
          <Plus className="h-4 w-4" />
          Generate Link
        </Button>
      </DialogTrigger>
      <DialogContent className={`sm:max-w-[500px] ${isLightMode ? 'bg-white text-gray-800 border-gray-200' : 'bg-[#1a1a1a] text-white border-gray-800'}`}>
        <DialogHeader>
          <DialogTitle className={isLightMode ? 'text-gray-800' : 'text-white'}>Generate Onboarding Link</DialogTitle>
          <DialogDescription className={isLightMode ? 'text-gray-600' : 'text-gray-400'}>
            {!generatedLink ? 
              "Create a new onboarding link for a content creator to fill out their details" :
              "Your onboarding link has been generated"
            }
          </DialogDescription>
        </DialogHeader>
        
        {!generatedLink ? (
          <div className="space-y-4 py-4">
            <p className={`text-sm ${isLightMode ? 'text-gray-700' : 'text-gray-300'}`}>
              Clicking "Generate Link" will create a unique onboarding form that you can share with content creators. 
              They will fill in all the necessary details through this form.
            </p>
            <DialogFooter className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                disabled={isLoading}
                className={isLightMode ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-gray-700 text-gray-200 hover:bg-gray-800'}
              >
                Cancel
              </Button>
              <Button 
                onClick={generateLink}
                disabled={isLoading}
                className={isLightMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
              >
                {isLoading ? "Generating..." : "Generate Link"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Input 
                value={generatedLink} 
                readOnly 
                className={isLightMode ? 'bg-gray-100 text-gray-800 border-gray-300' : 'bg-gray-800 text-white border-gray-700'}
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={copyToClipboard}
                className={isLightMode ? 'flex-shrink-0 border-gray-300 hover:bg-gray-100' : 'flex-shrink-0 border-gray-700 hover:bg-gray-800'}
              >
                <Copy className={`h-4 w-4 ${isLightMode ? 'text-gray-700' : 'text-gray-200'}`} />
              </Button>
            </div>
            <DialogFooter>
              <Button 
                onClick={closeAndRefresh}
                className={isLightMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
